import { useEffect, useCallback } from 'react';
import { useTracking } from 'react-tracking';

import { Grid, PlusSolid } from '@simplywallst/ui-core';

import {
  useCompanyNoteModal,
  Note,
  type NoteFragmentType,
} from '@/components/Note';
import { useCompanyNotes } from '@/components/Note';

import { EmptyState } from './components/EmptyState';
import { AddNoteButton } from './styled';

interface Props {
  companyId?: string;
  uniqueSymbol?: string;
}

export const Notes = ({ companyId, uniqueSymbol }: Props) => {
  const { openNoteModal, modalState } = useCompanyNoteModal();

  const canCreateNote = !!companyId && !!uniqueSymbol;
  const { data: notes } = useCompanyNotes(companyId);
  const hasNotes = notes && notes.length > 0;

  useEffect(() => {
    if (canCreateNote && modalState === 'adding') {
      openNoteModal({ companyId, uniqueSymbol });
    }
  }, [companyId, uniqueSymbol, modalState]);

  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'Note',
    companyId,
    uniqueSymbol,
  });

  const handleAddNote = useCallback(() => {
    if (!canCreateNote) {
      return;
    }
    trackEvent({
      action: 'click',
      modifier: 'add-note',
    });
    openNoteModal({ companyId, uniqueSymbol });
  }, [companyId, uniqueSymbol]);

  const handleEditNote = useCallback(
    (note: NoteFragmentType) => {
      if (!canCreateNote) {
        return;
      }
      openNoteModal({ note, companyId, uniqueSymbol });
    },
    [companyId, uniqueSymbol]
  );

  return (
    <Track>
      <Grid data-cy-id="notes" gap="8px">
        {hasNotes ? (
          <>
            {notes.map((n) => (
              <Note key={`note-${n.id}`} note={n} editNote={handleEditNote} />
            ))}
            <AddNoteButton styleType="louder" onClick={handleAddNote}>
              <PlusSolid height={16} width={16} />
              &nbsp; Add Note
            </AddNoteButton>
          </>
        ) : (
          <EmptyState
            handleAddNote={handleAddNote}
            canCreateNote={canCreateNote}
          />
        )}
      </Grid>
    </Track>
  );
};
